import { useEffect, useState } from "react";

import { GlobalStyles } from "@mui/material";
import { ThemeProvider, Theme, StyledEngineProvider } from "@mui/material/styles";
import { AppCacheProvider } from "@mui/material-nextjs/v15-pagesRouter";
import { LicenseInfo } from "@mui/x-license-pro";
import { useRouter } from "next/router";
// eslint-disable-next-line no-restricted-imports
import { appWithTranslation } from "next-i18next";
import { SECURITY_EXEMPTION_URL_PARAM } from "@simplyk/common";

import { ANONYMIZE_SENSITIVE_DATA_KEY, getFromLocalStorage } from "../common/helpers/localStorage";
import { getSecurityExemptionToken } from "../common/helpers/securityExemption";
import { trpc } from "../common/helpers/trpc";
import { isTurnstileExcludedRoute } from "../common/helpers/turnstileExcludedRoutes";
import { ConsentModeProvider } from "../common/providers/ConsentModeProvider";
import { DatadogProvider } from "../common/providers/DatadogProvider";
import { SessionContextProvider } from "../common/providers/SessionProvider";

import { QueryStateContextProvider } from "@/common/hooks/useQueryStateContext";
import { SessionRequiredNotice } from "@/common/components/SessionRequiredNotice/SessionRequiredNotice";
import { useTurnstileChallenge } from "@/common/hooks/useTurnstileChallenge";
import { zeffyTheme, superAdminTheme } from "@/common/theme/theme";
import { MyAppProps } from "@/common/types/page";
import { ChatWidgetVisibilityManager } from "@/components/ChatWidgetVisibilityManager/ChatWidgetVisibilityManager";
import { withClientTranslations } from "@/components/ClientTranslations/withClientTranslations";
import { ErrorHandler } from "@/components/ErrorHandler/ErrorHandler";
import { ZeffySnackbarProvider } from "@/dashboard-design-system/Snackbar";

import "../styles.css";
import "react-image-crop/dist/ReactCrop.css";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_X_LICENSE_KEY as string);

export enum WebVitals {
  "FCP" = "FCP",
  "FID" = "FID",
}

export function reportWebVitals(metric: { id: string; name: string; label: string; value: number; startTime: number }) {
  const { id, name, label, value } = metric;

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log("Web vitals:", name, value);
    return;
  }

  if (!window.gtag) {
    // eslint-disable-next-line no-console
    console.error("window.gtag is not defined, verify that gtag script is imported via <Script> or via gTagManager");
    return;
  }

  window.gtag("event", name, {
    event_category: label === "web-vital" ? "Web Vitals" : "Next.js custom metric",
    value: Math.round(name === "CLS" ? value * 1000 : value), // values must be integers
    event_label: id, // id unique to current page load
    non_interaction: true, // avoids affecting bounce rate.
  });
}

const MyApp = (props: MyAppProps) => {
  const { Component, pageProps, emotionCache, ...otherProps } = props;
  const [isAdminTheme, setIsAdminTheme] = useState(false);
  const router = useRouter();

  useTurnstileChallenge({ enabled: !isTurnstileExcludedRoute(router.pathname) });

  useEffect(() => {
    if (!router.asPath.includes(SECURITY_EXEMPTION_URL_PARAM)) {
      return;
    }
    getSecurityExemptionToken();
    // Keeps the load-test token out of shared links, analytics and the address bar once it is stored for the tab.
    const url = new URL(router.asPath, window.location.origin);
    url.searchParams.delete(SECURITY_EXEMPTION_URL_PARAM);
    void router.replace(`${url.pathname}${url.search}${url.hash}`, undefined, { shallow: true });
  }, [router]);

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles?.parentElement?.removeChild(jssStyles);
    }
  }, []);

  useEffect(() => {
    const syncAnonymize = () => {
      const enabled = getFromLocalStorage?.(ANONYMIZE_SENSITIVE_DATA_KEY) === "true";
      document.body.classList.toggle("anonymize-sensitive-data", enabled);
    };

    syncAnonymize();
    window.addEventListener("storage", syncAnonymize);
    return () => window.removeEventListener("storage", syncAnonymize);
  }, []);

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page: React.ReactElement<unknown>, _pageProps?: unknown) => page);

  return (
    <AppCacheProvider emotionCache={emotionCache} {...otherProps}>
      <QueryStateContextProvider>
        <ErrorHandler>
          <SessionContextProvider setIsAdminTheme={setIsAdminTheme}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={isAdminTheme ? superAdminTheme : zeffyTheme}>
                <GlobalStyles
                  styles={{
                    "@keyframes mui-auto-fill": { from: { display: "block" } },
                    "@keyframes mui-auto-fill-cancel": { from: { display: "block" } },
                  }}
                />
                <ZeffySnackbarProvider>
                  <SessionRequiredNotice />
                  <ConsentModeProvider>
                    <DatadogProvider>
                      <>{getLayout(<Component {...pageProps} />, pageProps)}</>
                      <ChatWidgetVisibilityManager />
                    </DatadogProvider>
                  </ConsentModeProvider>
                </ZeffySnackbarProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </SessionContextProvider>
        </ErrorHandler>
      </QueryStateContextProvider>
    </AppCacheProvider>
  );
};

export default trpc.withTRPC(withClientTranslations(appWithTranslation(MyApp)));
